// /call-schedule/availability — manage weekly open hours (rules) and time off
// (blocks). Bookable slots are computed as: rules − blocks − existing bookings.
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  FaCalendarAlt, FaClock, FaBan, FaPlus, FaEdit, FaTrash, FaSpinner, FaInfoCircle, FaPowerOff,
} from 'react-icons/fa';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Modal from '../../../components/shared/Modal';
import ModalNotification from '../../../components/shared/ModalNotification';
import RuleEditor from '../components/RuleEditor';
import BlockEditor from '../components/BlockEditor';
import {
  listRules, deleteRule, listBlocks, deleteBlock,
} from '../callScheduleService';
import { DAY_NAMES, formatEastern } from '../utils/scheduleUtils';

const PageHeader = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: ${p => p.theme.spacing(2)};
  h1 { color: ${p => p.theme.colors.primary}; margin: 0; font-size: 1.4rem; }
  svg.title-icon { color: ${p => p.theme.colors.primary}; font-size: 1.3rem; }
`;

const Note = styled.div`
  display: flex; gap: 8px; align-items: flex-start;
  background: ${p => p.theme.colors.primary}14; color: ${p => p.theme.colors.textSecondary};
  border-radius: ${p => p.theme.borderRadius}; padding: 10px 14px; font-size: 0.82rem;
  line-height: 1.45; margin-bottom: ${p => p.theme.spacing(3)};
  svg { color: ${p => p.theme.colors.primary}; flex-shrink: 0; margin-top: 2px; }
`;

const Card = styled.div`
  background: ${p => p.theme.colors.surface}; border-radius: 12px;
  padding: ${p => p.theme.spacing(3)}; margin-bottom: ${p => p.theme.spacing(3)};
`;

const CardHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: ${p => p.theme.spacing(2)};
  h3 { color: ${p => p.theme.colors.primary}; margin: 0; font-size: 1.05rem; display: flex; align-items: center; gap: 8px; }
`;

const AddBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; padding: 8px 13px; border: none; border-radius: 8px;
  background: ${p => p.theme.colors.primary}; color: #fff; font-weight: 600; font-size: 0.82rem; cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 0.86rem;
  th, td { padding: 10px 12px; text-align: left; border-bottom: 1px solid ${p => p.theme.colors.inputGray}22; }
  th { color: ${p => p.theme.colors.textSecondary}; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px; }
  td { color: ${p => p.theme.colors.textPrimary}; }
`;

const Pill = styled.span`
  font-size: 0.72rem; font-weight: 700; padding: 2px 9px; border-radius: 10px;
  background: ${p => (p.$on ? '#66BB6A' : '#999')}22; color: ${p => (p.$on ? '#66BB6A' : '#999')};
`;

const IconBtn = styled.button`
  background: transparent; border: 1px solid ${p => p.theme.colors.inputGray}44; border-radius: 7px;
  color: ${p => (p.$danger ? p.theme.colors.error : p.theme.colors.textSecondary)};
  padding: 5px 9px; margin-right: 6px; cursor: pointer; font-size: 0.78rem;
  &:hover { border-color: ${p => (p.$danger ? p.theme.colors.error : p.theme.colors.primary)}; color: ${p => (p.$danger ? p.theme.colors.error : p.theme.colors.primary)}; }
`;

const Empty = styled.div`text-align: center; padding: 28px; color: #999; font-size: 0.88rem;`;

const fmtTime12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

const AvailabilityPage = () => {
  const [rules, setRules] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [showPastBlocks, setShowPastBlocks] = useState(false);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });

  const [ruleModal, setRuleModal] = useState(null);   // { rule } | { rule: null }
  const [blockModal, setBlockModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind, item }

  const notify = (message, success = true) => setFeedback({ show: true, message, success });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, blocksRes] = await Promise.all([listRules(), listBlocks(showPastBlocks)]);
      const sorted = (rulesRes.rules || []).slice().sort(
        (a, b) => a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time)
      );
      setRules(sorted);
      setBlocks(blocksRes.blocks || []);
    } catch (err) {
      notify('Failed to load availability', false);
    } finally {
      setLoading(false);
    }
  }, [showPastBlocks]);

  useEffect(() => { load(); }, [load]);

  const onSaved = (msg) => { notify(msg); load(); };

  const confirmDelete = async () => {
    const { kind, item } = deleteTarget;
    try {
      if (kind === 'rule') await deleteRule(item.id);
      else await deleteBlock(item.id);
      setDeleteTarget(null);
      notify(kind === 'rule' ? 'Availability removed' : 'Time off removed');
      load();
    } catch (err) {
      setDeleteTarget(null);
      notify(err.response?.data?.message || 'Failed to delete', false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <FaCalendarAlt className="title-icon" />
        <h1>Availability</h1>
      </PageHeader>

      <Note>
        <FaInfoCircle />
        <span>Bookable slots leads see on the website are generated from these <strong>weekly hours</strong>, minus any <strong>time off</strong> and minus slots that are already booked. All times are Eastern (America/New_York).</span>
      </Note>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px', color: '#F69620' }}>
          <FaSpinner style={{ fontSize: 32, animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <>
          <Card>
            <CardHead>
              <h3><FaClock /> Weekly Hours</h3>
              <AddBtn onClick={() => setRuleModal({ rule: null })}><FaPlus /> Add Hours</AddBtn>
            </CardHead>
            {rules.length === 0 ? (
              <Empty>No weekly hours set. Add hours so leads can book calls.</Empty>
            ) : (
              <Table>
                <thead>
                  <tr><th>Day</th><th>Hours</th><th>Slot</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {rules.map(rule => (
                    <tr key={rule.id}>
                      <td style={{ fontWeight: 500 }}>{DAY_NAMES[rule.day_of_week]}</td>
                      <td>{fmtTime12(rule.start_time)} – {fmtTime12(rule.end_time)}</td>
                      <td>{rule.slot_minutes} min</td>
                      <td><Pill $on={rule.is_active}><FaPowerOff size={9} /> {rule.is_active ? 'Active' : 'Disabled'}</Pill></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <IconBtn onClick={() => setRuleModal({ rule })}><FaEdit /> Edit</IconBtn>
                        <IconBtn $danger onClick={() => setDeleteTarget({ kind: 'rule', item: rule })}><FaTrash /> Delete</IconBtn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card>
            <CardHead>
              <h3><FaBan /> Time Off</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#ccc', cursor: 'pointer' }}>
                  <input type="checkbox" checked={showPastBlocks} onChange={(e) => setShowPastBlocks(e.target.checked)} />
                  Show past
                </label>
                <AddBtn onClick={() => setBlockModal({ block: null })}><FaPlus /> Add Time Off</AddBtn>
              </div>
            </CardHead>
            {blocks.length === 0 ? (
              <Empty>No time off scheduled.</Empty>
            ) : (
              <Table>
                <thead>
                  <tr><th>Starts</th><th>Ends</th><th>Reason</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {blocks.map(block => (
                    <tr key={block.id}>
                      <td>{formatEastern(block.starts_at)}</td>
                      <td>{formatEastern(block.ends_at)}</td>
                      <td>{block.reason || <span style={{ color: '#999' }}>—</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <IconBtn onClick={() => setBlockModal({ block })}><FaEdit /> Edit</IconBtn>
                        <IconBtn $danger onClick={() => setDeleteTarget({ kind: 'block', item: block })}><FaTrash /> Delete</IconBtn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      )}

      <RuleEditor
        isOpen={!!ruleModal}
        rule={ruleModal?.rule || null}
        onClose={() => setRuleModal(null)}
        onSaved={onSaved}
      />
      <BlockEditor
        isOpen={!!blockModal}
        block={blockModal?.block || null}
        onClose={() => setBlockModal(null)}
        onSaved={onSaved}
      />

      <Modal
        title={deleteTarget?.kind === 'rule' ? 'Remove Weekly Hours' : 'Remove Time Off'}
        isOpen={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      >
        <p style={{ color: '#ccc', lineHeight: 1.6 }}>
          {deleteTarget?.kind === 'rule'
            ? <>Remove <strong style={{ color: '#fff' }}>{DAY_NAMES[deleteTarget?.item?.day_of_week]}</strong> hours? Leads will no longer be able to book that day.</>
            : <>Remove this time-off block? Slots in that window become bookable again.</>}
        </p>
      </Modal>

      <ModalNotification
        isOpen={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DashboardLayout>
  );
};

export default AvailabilityPage;
